import { useEffect } from 'react';
import { useParams } from "react-router-dom";
import { useDispatch, useSelector, batch } from 'react-redux';
import { EventsState } from '../../../../../store/types';
import { getSessionDeviceTypes, getSessionDuration, getSessionRegions, getSessionRetentionAverage, getSessionViews, getSessionRegistrations, getSessionWatchtime, getSessionBrowsers, getSessionEmails, getSessionLanguages, getSessionTimeline, getSessionCountryMap } from '../../../../../store/actions/admin/analytics';
import Scorecard from '../../components/Scorecard/Scorecard';
import ComponentWithHeader from '../../components/Scaffolding/ComponentWithHeader';
import AnimatedPieSVG from '../../components/PieChart/PieChartAnimated';
// import BarChartAudienceLocation from '../../components/BarChart/BarChartAudienceLocation';
// import BarChartAudienceRetention from '../../components/BarChart/BarChartAudienceRetention';
// import AudienceMap from '../../components/Map/AudienceMap';
import SessionTimeline from '../../components/SessionTimeline/SessionTimeline';
import usePrevious from '../../../../../utils/use-previous';
import AudienceMap from '../../components/Map/AudienceMap';
import BarChartAudienceLocation from '../../components/BarChart/BarChartAudienceLocation';
import { tooltipText } from './ClientLevelEventAnalytics';

interface ParamsProps {
	eventUuid: string,
	sessionUuid: string;
}

const SessionLevelEventAnalytics: React.FC = () => {

	const {
		AuthReducer: { user, token },
		AnalyticsReducer: { sessionDevices, sessionRegions, sessionLanguages, sessionViews, sessionDuration, sessionRetentionAverage, sessionRegistrations, sessionWatchtime, sessionBrowsers, sessionEmails, sessionTimeline, sessionMapData }
	} = useSelector((state: EventsState) => state);

	const { eventUuid, sessionUuid }: ParamsProps = useParams();

	const dispatch = useDispatch();

	const lastChannel = usePrevious(user?.active_channel);

	useEffect(() => {
		if (lastChannel != user?.active_channel) {
			batch(() => {
				dispatch(getSessionDeviceTypes(sessionUuid, token!));
				dispatch(getSessionRegions(sessionUuid, token!));
				dispatch(getSessionLanguages(sessionUuid, token!));
				dispatch(getSessionViews(sessionUuid, token!));
				dispatch(getSessionDuration(sessionUuid, token!));
				dispatch(getSessionRetentionAverage(sessionUuid, token!));
				dispatch(getSessionRegistrations(sessionUuid, token!));
				dispatch(getSessionEmails(sessionUuid, token!));
				dispatch(getSessionWatchtime(sessionUuid, token!));
				dispatch(getSessionBrowsers(sessionUuid, token!));
				dispatch(getSessionTimeline(sessionUuid, token!));
				dispatch(getSessionCountryMap(sessionUuid, token!));
			});
		}
	}, [user, lastChannel, token, eventUuid, sessionUuid, dispatch]);

	return (

		<div className="data-analytics">
			<div className="session-metrics-grid">
				<Scorecard data={sessionViews.data} index={0} showTL={false} showMM={false} title="Total Views" loading={sessionViews.loading} error={sessionViews.error} tooltipText={tooltipText.totalViews} />
				<Scorecard data={sessionDuration.data} index={1} showTL={false} showMM={false} title="Avg. Duration, min" loading={sessionDuration.loading} error={sessionDuration.error} tooltipText={tooltipText.avgDuration} />
				<Scorecard data={sessionRetentionAverage.data} index={2} showTL={false} showMM={false} title="Retention, %" loading={sessionRetentionAverage.loading} error={sessionRetentionAverage.error} tooltipText={tooltipText.retentionPercent} />
				<Scorecard data={sessionRegistrations.data} index={3} showTL={true} showMM={false} title="Total Registrations" loading={sessionRegistrations.loading} error={sessionRegistrations.error} tooltipText={tooltipText.totalRegistrations} />
				<Scorecard data={sessionWatchtime.data} index={4} showTL={true} showMM={false} title="Total Watchtime, min" loading={sessionWatchtime.loading} error={sessionWatchtime.error} tooltipText={tooltipText.totalWatchtime} />
				{/* <Scorecard data={scorecards.pageviews} index={5} showTL={true} showMM={false} title="Page Views" /> */}
				{/* <Scorecard data={scorecards.interactionScore} index={6} showTL={true} showMM={false} title="Interaction Score" /> */}
				<Scorecard data={sessionEmails.data} index={7} showTL={false} showMM={false} title="Emails" loading={sessionEmails.loading} error={sessionEmails.error} tooltipText={tooltipText.emails} />
				{/* <Scorecard data={scorecards.sessions} index={8} showTL={false} showMM={false} title="Sessions" /> */}

				<ComponentWithHeader X={"X2"} Y={"Y2"} id={["donut-chart", 0]} header={"Language"} tooltipText={tooltipText.languages}>
					<AnimatedPieSVG data={{ values: sessionLanguages.data }} index={0} title={"Language"} loading={sessionLanguages.loading} error={sessionLanguages.error} />
				</ComponentWithHeader>

				<ComponentWithHeader X={"X2"} Y={"Y2"} id={["donut-chart", 1]} header={"Region"} tooltipText={tooltipText.region}>
					<AnimatedPieSVG data={{ values: sessionRegions.data }} index={1} title={"Region"} loading={sessionRegions.loading} error={sessionRegions.error} />
				</ComponentWithHeader>

				<ComponentWithHeader X={"X2"} Y={"Y2"} id={["donut-chart", 2]} header={"Browser"} tooltipText={tooltipText.browser}>
					<AnimatedPieSVG data={{ values: sessionBrowsers.data }} index={2} title={"Browser"} loading={sessionBrowsers.loading} error={sessionBrowsers.error} />
				</ComponentWithHeader>

				<ComponentWithHeader X={"X2"} Y={"Y2"} id={["donut-chart", 3]} header={"Devices"} tooltipText={tooltipText.deviceType}>
					<AnimatedPieSVG data={{ values: sessionDevices.data }} index={3} title={"Devices"} loading={sessionDevices.loading} error={sessionDevices.error} />
				</ComponentWithHeader>

				{/* <ComponentWithHeader X={"X6"} Y={"Y4"} id={["event-retention", 0]} header={"Audience Retention"}> */}
				{/* <BarChartAudienceRetention width={720} height={320} dataIn={retentionData}/> */}
				{/* </ComponentWithHeader> */}

				<ComponentWithHeader X={"X6"} Y={"Y4"} id={["session-timeline", 0]} header={"Session Timeline"} tooltipText={tooltipText.timeline}>
					<SessionTimeline width={720} height={320} dataIn={sessionTimeline.data} loading={sessionTimeline.loading} error={sessionTimeline.error} />
				</ComponentWithHeader>

				<ComponentWithHeader X={"X6"} Y={"Y4"} id={["world-map", 0]} header={"Audience Location"} tooltipText={tooltipText.audienceLocation}>
					<AudienceMap width={408} height={280} countryMapData={sessionMapData.data} loading={sessionMapData.loading} error={sessionMapData.error} />
					<BarChartAudienceLocation data={sessionMapData.data} loading={sessionMapData.loading} error={sessionMapData.error} />
				</ComponentWithHeader>

			</div>
		</div>
	);
};

export default SessionLevelEventAnalytics;
